import { z } from 'zod';
import { FlowTestAssertionSchema } from './assertion.js';
import { FlowTestBlockerSchema, FlowTestPreflightSchema } from './preflight.js';
import { FlowTestResultSchema } from './result.js';
import { FlowTestStartPayloadSchema } from './startPayload.js';
import { FlowTestBlockMocksByIdSchema, FlowTestExpectedRunStateSchema, RunnableFlowTestCaseSchema, } from './testCase.js';
import { FlowRunState } from '../flowRun/flowRunState.js';
import { FlowApiPageLimitSchema, FlowApiPageOffsetSchema } from '../pagination/pageLimit.js';
/** `assertion` is absent when this build cannot read the stored row, such as a legacy comparator. */
export const FlowTestAssertionViewSchema = z.object({
    id: z.uuid(),
    blockId: z.string(),
    name: z.string(),
    assertion: FlowTestAssertionSchema.optional(),
});
export const FlowTestRunViewSchema = z.object({
    flowRunId: z.uuid(),
    state: z.enum(FlowRunState),
    /** Null while the run is being judged; canceled runs are never judged. */
    result: FlowTestResultSchema.nullable(),
    message: z.string().nullable(),
    created: z.iso.datetime(),
    startedAt: z.iso.datetime().nullable(),
    endedAt: z.iso.datetime().nullable(),
});
export const FlowTestCaseViewSchema = z.object({
    id: z.uuid(),
    flowId: z.uuid(),
    flowDefinitionId: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    startPayload: FlowTestStartPayloadSchema,
    expectedRunState: FlowTestExpectedRunStateSchema,
    blockOutputMocks: FlowTestBlockMocksByIdSchema,
    /** An archived case stays readable. */
    archivedAt: z.iso.datetime().nullable(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    assertions: z.array(FlowTestAssertionViewSchema),
    latestRun: FlowTestRunViewSchema.nullable(),
});
/** Names every block the case can reach, including those in sub-flows the root definition does not list. */
export const FlowTestCasePreflightViewSchema = FlowTestPreflightSchema.extend({
    blockNamesById: z.record(z.string(), z.string()),
});
export const FindAllFlowTestCasesSchema = z.object({
    flowDefinitionId: z.uuid(),
});
export const FindAllFlowTestRunsSchema = z.object({
    limit: FlowApiPageLimitSchema.default(50),
    offset: FlowApiPageOffsetSchema.default(0),
});
export const CreateFlowTestCaseSchema = RunnableFlowTestCaseSchema.omit({
    blockConfigMocks: true,
    liveBlockIds: true,
}).extend({
    flowDefinitionId: z.uuid(),
});
export const DeleteFlowTestCaseResponseSchema = z.object({
    id: z.uuid(),
    archivedAt: z.iso.datetime(),
});
export const StartFlowTestRunResponseSchema = z.object({
    testCaseId: z.uuid(),
    flowRunId: z.uuid(),
});
/** The most test cases one suite-run request may start. */
export const FLOW_TEST_SUITE_RUN_MAX_CASES = 50;
export const RunFlowTestSuiteSchema = z.object({
    flowDefinitionId: z.uuid(),
    /** Omitted to run the flow's whole suite. */
    testCaseIds: z.array(z.uuid()).min(1).optional(),
});
export const RunFlowTestSuiteResponseSchema = z.object({
    started: z.array(StartFlowTestRunResponseSchema),
    skipped: z.array(z.object({
        testCaseId: z.uuid(),
        reason: z.string(),
        blockers: z.array(FlowTestBlockerSchema).optional(),
    })),
});
export const FlowTestAssertionResultViewSchema = z.object({
    assertionId: z.uuid(),
    blockId: z.string(),
    name: z.string(),
    result: FlowTestResultSchema,
    /** One entry per judged occurrence of the block; null on results recorded before actual values were. */
    actualValues: z.array(z.unknown()).nullable(),
    message: z.string().nullable(),
});
export const FlowTestRunDetailSchema = FlowTestRunViewSchema.extend({
    testCaseId: z.uuid(),
    assertionResults: z.array(FlowTestAssertionResultViewSchema),
});
//# sourceMappingURL=api.js.map